import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { AdminAuctionQuestion } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { useAnswerQuestion } from '../hooks/use-questions';
import {
  answerSchema,
  type AnswerFormInput,
  type AnswerFormOutput,
} from '../schemas/sale-form-schema';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['answer']);

/** The answer goes on the lot page for everyone and by email to the asker; it cannot be edited. */
export function AnswerQuestionDialog({
  storeId,
  question,
  onOpenChange,
}: {
  storeId: string;
  question: AdminAuctionQuestion | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation(['auctions', 'common']);
  const message = useErrorMessage();
  const answer = useAnswerQuestion(storeId);
  const form = useForm<AnswerFormInput, unknown, AnswerFormOutput>({
    resolver: useI18nResolver(answerSchema),
    defaultValues: { answer: '' },
  });

  const close = () => {
    onOpenChange(false);
    form.reset({ answer: '' });
  };

  const submit = form.handleSubmit(async (values) => {
    if (!question) return;
    try {
      await answer.mutateAsync({ id: question.id, answer: values.answer });
      close();
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) return;
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });

  return (
    <Dialog open={question !== null} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('auctions:questions.answer.title')}</DialogTitle>
          <DialogDescription>
            {question
              ? t('auctions:questions.answer.description', {
                  bidder: question.bidderLabel ?? question.customerName ?? question.customerEmail,
                  lot: question.lot.title,
                })
              : ''}
          </DialogDescription>
        </DialogHeader>
        {question ? (
          <blockquote className="bg-muted/60 rounded-md border-l-2 p-3 text-sm whitespace-pre-wrap">
            {question.question}
          </blockquote>
        ) : null}
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {form.formState.errors.root?.server?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
              </Alert>
            ) : null}
            <FormField
              control={form.control}
              name="answer"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('auctions:questions.answer.label')}</FormLabel>
                  <FormControl>
                    <Textarea rows={5} autoFocus {...field} />
                  </FormControl>
                  <FormDescription>{t('auctions:questions.answer.hint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={answer.isPending}>
                {answer.isPending
                  ? t('auctions:actions.working')
                  : t('auctions:questions.answer.confirm')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
